import { apiService } from '@clarix/api-client';
import { pmsBase } from '../lib/base';
import { PMS_ENDPOINTS } from '../endpoints';
import type {
  CloseRunResult,
  RemindResult,
  CreateRunPayload,
  EvaluationRun,
  PublishRunResult,
  RunException,
  RunParticipant,
  RunPreview,
} from '../types';

const base = () => `${pmsBase()}${PMS_ENDPOINTS.RUNS}`;

/**
 * Run Management client (§7 of the frozen contract).
 *
 * All 13 paths are declared even though step 4's publish flow uses only
 * create → resolve → publish. The others are the read and post-publish surface
 * M4+ builds on, and declaring them here is what lets `pnpm pms:contract` diff
 * the FRONTEND against the contract rather than only the backend — its frontend
 * column was previously blind to every path composed at a call site.
 */
export const runsApi = {
  list: (status?: string) =>
    apiService.get<EvaluationRun[]>(
      status ? `${base()}?status=${encodeURIComponent(status)}` : base(),
    ),

  get: (runId: string) => apiService.get<EvaluationRun>(`${base()}/${runId}`),

  create: (data: CreateRunPayload) => apiService.post<EvaluationRun>(base(), data),

  update: (runId: string, data: Partial<CreateRunPayload>) =>
    apiService.put<EvaluationRun>(`${base()}/${runId}`, data),

  /** DELETE cancels the run (FR-206); the record is kept for audit. */
  cancel: (runId: string) => apiService.delete<{ run: EvaluationRun }>(`${base()}/${runId}`),

  /**
   * Expand the audience and resolve every evaluator (FR-202/FR-203). A WRITE:
   * it materialises participant rows and the exception log.
   */
  resolve: (runId: string) => apiService.post<RunPreview>(`${base()}/${runId}/resolve`),

  preview: (runId: string) => apiService.get<RunPreview>(`${base()}/${runId}/preview`),

  /** FR-205 — atomic on the server: snapshots, participants and tokens together. */
  publish: (runId: string) => apiService.post<PublishRunResult>(`${base()}/${runId}/publish`),

  exceptions: (runId: string) =>
    apiService.get<RunException[]>(`${base()}/${runId}/exceptions`),

  // ── Post-publish management (FR-206) ────────────────────────────────
  // No UI yet: the prototype's published step 4 shows the evaluation link and
  // nothing else, so these stay API-only until a surface for them is specified.

  overrideEvaluator: (
    runId: string,
    participantId: string,
    data: { evaluator_employee_id: string; reason: string },
  ) =>
    apiService.put<RunParticipant>(
      `${base()}/${runId}/participants/${participantId}/override`,
      data,
    ),

  reassignEvaluator: (
    runId: string,
    participantId: string,
    data: { evaluator_employee_id: string; reason: string },
  ) =>
    apiService.put<RunParticipant>(
      `${base()}/${runId}/participants/${participantId}/reassign-evaluator`,
      data,
    ),

  resendInvitation: (runId: string, participantId: string) =>
    apiService.post<RunParticipant>(
      `${base()}/${runId}/participants/${participantId}/resend-invitation`,
    ),

  /** `role` says WHICH deadline moves — the employee's or the evaluator's (D-20). */
  extendParticipantExpiry: (
    runId: string,
    participantId: string,
    newExpiryAt: string,
    role: 'Self' | 'Evaluator' = 'Self',
  ) =>
    apiService.post<RunParticipant>(
      `${base()}/${runId}/participants/${participantId}/extend-expiry`,
      { new_expiry_at: newExpiryAt, role },
    ),

  extendRunExpiry: (runId: string, newExpiryAt: string, role: 'Self' | 'Evaluator' = 'Self') =>
    apiService.post<{ run: EvaluationRun; extended_count: number }>(
      `${base()}/${runId}/extend-expiry`,
      { new_expiry_at: newExpiryAt, role },
    ),

  addParticipant: (runId: string, employeeId: string) =>
    apiService.post<RunParticipant>(`${base()}/${runId}/add-participant`, {
      employee_id: employeeId,
    }),

  /**
   * FR-207 — chase whoever still owes an answer. `role` narrows it to one half
   * of the cycle; the default reminds everyone holding it up.
   */
  remind: (runId: string, role: 'Self' | 'Evaluator' | 'Both' = 'Both') =>
    apiService.post<RemindResult>(`${base()}/${runId}/remind`, { role }),

  /** Take one person out of a live cycle. The reason is mandatory. */
  removeParticipant: (runId: string, participantId: string, reason: string) =>
    apiService.post<{ participant: RunParticipant; run_auto_closed: boolean }>(
      `${base()}/${runId}/participants/${participantId}/remove`,
      { reason },
    ),

  /** FR-209 force-close. The confirmation is required by the contract. */
  close: (runId: string) =>
    apiService.post<CloseRunResult>(`${base()}/${runId}/close`, { confirm: true }),
};
